<script lang="ts">
  import { getStoresContext, type StoresKey } from "~/stores";
  import Swiper from 'swiper';
  import { Navigation } from 'swiper/modules';
  import { onMount, onDestroy } from 'svelte';
  import { currentDayHours } from '~/theme/demo2025/stores/scOpeningHours';
  import { fly } from 'svelte/transition';

  export let request_store_key: StoresKey;
  export let storesCollectionUrl: string;
  export let openingHoursUrl: string;
  export let storeHoursVaryText: string;
  export let highlightedStores: any[] = [];
  export let todaysHoursText: string = '';
  export let tomorrowsHoursText: string = '';
  export let style: string;
  let _class;
  export { _class as class };
  const stores_context = getStoresContext(request_store_key);
  const { T, opening_hours, locale } = stores_context;

  $: t = $T;

  let swiperEl: HTMLElement;  
  let swiperInstance: Swiper | null = null;
  let isClient = false;
  let containerWidth = 0;
  let textContainer: HTMLElement;
  let isBeginning = true;
  let isEnd = false;
  let isMobile = false;
  let dailySchedule: DailySchedule[] = [];
  let currentSlide = 0;
  let currentStoreIndex = 0;
  let storeAnimationInterval: number | null = null;
  let showStore = true;
  let previousHoursText = '';
  let updateTimeout: ReturnType<typeof setTimeout> | null = null;

  $: formattedHighlightedStores = highlightedStores.map(store => {
    const hours = store.unformattedOpeningHours && 
                 Array.isArray(store.unformattedOpeningHours) && 
                 store.unformattedOpeningHours.length > 0 ? 
                 store.unformattedOpeningHours[0] : null;
    
    return {
      id: store.id,
      name: store.name,
      openingStatus: hours?.openingStatus || 'CLOSED',
      hours: hours && hours.openingStatus === 'OPEN' && hours.dailyOpeningHours && 
             hours.dailyOpeningHours.length > 0 ? 
             `${hours.dailyOpeningHours[0].from}-${hours.dailyOpeningHours[0].to}` : 
             t`common.closed`
    };
  });

  interface DailySchedule {
    day: string;
    dayNumber: string;
    opens: string;
    closes: string;
    status: string;
  }
  
  function startStoreAnimation() {
    if (formattedHighlightedStores.length <= 1) return null;
    
    showStore = true;
    
    const interval = window.setInterval(() => {
      showStore = false;
      
      setTimeout(() => {
        currentStoreIndex = (currentStoreIndex + 1) % formattedHighlightedStores.length;
        showStore = true;
      }, 300);
    }, 4000); // Change store every 4 seconds
    
    return interval;
  }

  function createDailySchedule(openingHours: any[]): DailySchedule[] {
    if (!openingHours?.length) return [];
    
    let dailySchedule: DailySchedule[] = [];
    
    openingHours.forEach(group => {
      const days = group.days.split(',').map((d: string) => d.trim());
      
      days.forEach((dayNum: string) => {
        const dayNumInt = parseInt(dayNum);
        // Convert numeric day to weekday name using common format
        // 0 = sunday, 1 = monday, etc.
        const weekdayKey = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][dayNumInt];
        
        dailySchedule.push({
          day: t(`common.weekdays.${weekdayKey}`),
          dayNumber: dayNum,
          opens: group.dailyOpeningHours?.[0]?.from || '',
          closes: group.dailyOpeningHours?.[0]?.to || '',
          status: group.openingStatus || 'CLOSED'
        });
      });
    });

    dailySchedule.sort((a, b) => Number(a.dayNumber) - Number(b.dayNumber));

    // Only rotate if we're on client side and have data
    if (isClient && dailySchedule.length) {
      const today = new Date().getDay().toString();
      const todayIndex = dailySchedule.findIndex(schedule => schedule.dayNumber === today);
      
      if (todayIndex > -1) {
        dailySchedule = [
          ...dailySchedule.slice(todayIndex),
          ...dailySchedule.slice(0, todayIndex)
        ];
      }
    }

    return dailySchedule;
  }

  // Calculate the maximum width needed
  function calculateMaxWidth(schedule: DailySchedule[]) {
    if (!textContainer) return;
    
    const tempDiv = document.createElement('div');
    tempDiv.style.visibility = 'hidden';
    tempDiv.style.position = 'absolute';
    tempDiv.style.whiteSpace = 'nowrap';
    tempDiv.style.font = window.getComputedStyle(textContainer).font;
    document.body.appendChild(tempDiv);

    let maxWidth = 0;
    schedule.forEach((day, index) => {
      const text = `${index === 0 ? todaysHoursText :
                    index === 1 ? tomorrowsHoursText :
                    day.day} | ${day.status === 'CLOSED' ? t`common.closed` : `${day.opens}-${day.closes}`}`;
      tempDiv.textContent = text;
      maxWidth = Math.max(maxWidth, tempDiv.offsetWidth);
    });

    document.body.removeChild(tempDiv);
    containerWidth = maxWidth + 360;
  }

  function handlePrev() {
    if (swiperInstance && !isBeginning) {
      swiperInstance.slidePrev();
    }
  }

  function handleNext() {
    if (swiperInstance && !isEnd) {
      swiperInstance.slideNext();
    }
  }

  function updateCurrentDayHours(schedule: DailySchedule[]) {
    if (schedule.length > 0) {
      const todaySchedule = schedule[0];
      const hoursText = `${todaysHoursText} | ${todaySchedule.status === 'CLOSED' ? 
        t`common.closed` : 
        `${todaySchedule.opens}-${todaySchedule.closes}`}`;
      
      // Only update if the value has actually changed
      if (previousHoursText !== hoursText) {
        previousHoursText = hoursText;
        
        // Debounce the update to prevent rapid successive calls
        if (updateTimeout) {
          clearTimeout(updateTimeout);
        }
        
        updateTimeout = setTimeout(() => {
          currentDayHours.set(hoursText);
          updateTimeout = null;
        }, 100);
      }
    }
  }

  onMount(() => {
    isClient = true;
    // Check if mobile on mount
    isMobile = window.innerWidth <= 768;
    
    const handleResize = () => {
      isMobile = window.innerWidth <= 768;
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  onDestroy(() => {
    if (swiperInstance) {
      swiperInstance.destroy();
      swiperInstance = null;
    }
    
    if (storeAnimationInterval) {
      window.clearInterval(storeAnimationInterval);
      storeAnimationInterval = null;
    }
    
    if (updateTimeout) {
      clearTimeout(updateTimeout);
      updateTimeout = null;
    }
  });

  // Recalculate width when locale changes
  $: if (isClient && $locale) {
    setTimeout(() => {
      if (dailySchedule.length) {
        calculateMaxWidth(dailySchedule);
      }
    }, 0);
  }

  // Recalculate width when schedule changes
  $: if (isClient && dailySchedule.length) {
    setTimeout(() => calculateMaxWidth(dailySchedule), 0);
  }

  $: if (isClient) {
    dailySchedule = createDailySchedule($opening_hours?.shoppingCenterOpeningHours || []);
  }

  // Add this reactive statement to force update of button states
  $: if (swiperInstance) {
    isBeginning = swiperInstance.isBeginning;
    isEnd = swiperInstance.isEnd;
  }

  $: if (isClient && swiperEl && !swiperInstance && dailySchedule.length) {
    swiperInstance = new Swiper(swiperEl, {
      modules: [Navigation],
      slidesPerView: 1,
      navigation: false,
      loop: false,
      speed: 300,
      longSwipesRatio: 0.3,
      touchRatio: 1,
      preventClicks: true,
      preventClicksPropagation: true,
      initialSlide: 0,
      on: {
        slideChange: function(this: Swiper) {
          currentSlide = this.activeIndex;
          isBeginning = this.isBeginning;
          isEnd = this.isEnd;
        },
        init: function(this: Swiper) {
          currentSlide = this.activeIndex;
          isBeginning = this.isBeginning;
          isEnd = this.isEnd;
        }
      }
    });

    // Force immediate update of states after initialization
    isBeginning = swiperInstance.isBeginning;
    isEnd = swiperInstance.isEnd;
  }

  // Start animation when component mounts and when stores change
  $: if (isClient && formattedHighlightedStores.length > 0) {
    // Clear any existing interval first
    if (storeAnimationInterval) {
      window.clearInterval(storeAnimationInterval);
      storeAnimationInterval = null;
    }
    
    // Reset to first store and start animation
    currentStoreIndex = 0;
    showStore = true;
    storeAnimationInterval = startStoreAnimation();
  }

  // Update when either schedule or locale changes
  $: if (isClient && dailySchedule.length && $locale) {
    updateCurrentDayHours(dailySchedule);
  }
</script>

{#if dailySchedule.length}
  <div class="opening-hours-swiper {_class}" style="width: {containerWidth}px; {style}">
    <div class="opening-hours-swiper__container" bind:this={swiperEl} data-swiper-initialized={!!swiperInstance}>
      <div class="opening-hours-swiper__wrapper swiper-wrapper">
        {#each dailySchedule as schedule, index}
          <div class="opening-hours-swiper__slide swiper-slide">
            <div class="opening-hours-swiper__content" bind:this={textContainer}>
              <h2>
                <a href={openingHoursUrl} class="hours-link">
                  {index === 0 ? todaysHoursText :
                   index === 1 ? tomorrowsHoursText :
                   schedule.day} | {schedule.status === 'CLOSED' ? t`common.closed` : `${schedule.opens}-${schedule.closes}`}
                </a>
              </h2>
              {#if index === currentSlide}
                <a href={storesCollectionUrl} class="stores-link">{storeHoursVaryText}</a>
              {:else}
                <div class="stores-link disabled">{storeHoursVaryText}</div>
              {/if}
              
              {#if formattedHighlightedStores.length > 0 && index === currentSlide}
                <div class="highlighted-stores-carousel">
                  {#if formattedHighlightedStores[currentStoreIndex]}
                    {#if showStore}
                      <div 
                        class="highlighted-store" 
                        in:fly={{ x: 30, duration: 300, delay: 50 }}
                        out:fly={{ x: -30, duration: 300 }}
                      >
                        <strong>{formattedHighlightedStores[currentStoreIndex].name}</strong> {formattedHighlightedStores[currentStoreIndex].openingStatus === 'CLOSED' 
                          ? t`common.closed` 
                          : formattedHighlightedStores[currentStoreIndex].hours}
                      </div>
                    {/if}
                  {/if}
                </div>
              {/if}
            </div>
          </div>
        {/each}
      </div>
      <button 
        class="opening-hours-swiper__button-prev" 
        class:swiper-button-disabled={currentSlide === 0}
        aria-label="Previous"
        on:click={handlePrev}
      ></button>
      <button 
        class="opening-hours-swiper__button-next" 
        class:swiper-button-disabled={isEnd}
        aria-label="Next"
        on:click={handleNext}
      ></button>
    </div>
  </div>
{/if}

<style lang="scss">
  .opening-hours-swiper {
    max-width: 100%;
    margin: 0 auto;
    background: transparent;
    padding: 0;
    position: relative;

    &__container {
      width: 100%;
      margin: 0 auto;
    }

    &__content {
      text-align: center;
      padding: 1rem 40px;

      h2 {
        font-size: var(--font-size-title-desktop, 18px);
        font-weight: 600;
        line-height: 28px;
        letter-spacing: 1.5px;
        text-transform: uppercase;
        margin: 0;
        white-space: nowrap;
        
        .hours-link {
          color: inherit;
          text-decoration: none;
          
          &:hover {
            text-decoration: underline;
          }
        }
         @media screen and (max-width: 768px) {
          font-size: var(--font-size-title-mobile, 14px);
        }
      }

      .stores-link {
        display: block;
        font-size: var(--font-size-text-desktop, 16px);
        font-weight: 400;
        line-height: 18px;
        letter-spacing: 2px;
        text-transform: uppercase;
        margin: 0.5rem 0 0;
        white-space: nowrap;
        color: inherit;
        
        &:not(.disabled) {
          text-decoration: none;
          cursor: pointer;
          
          &:hover {
            text-decoration: underline;
          }
        }
        
        &.disabled {
          pointer-events: none;
          cursor: default;
        }

        @media screen and (max-width: 768px) {
          font-size: var(--font-size-text-mobile, 14px);
          white-space: normal;
        }
        @media screen and (max-width: 415px) {
          letter-spacing: 1px;
        }
      }
    }

    &__slide {
      opacity: 0;
      transition: opacity 0.3s ease;

      &:global(.swiper-slide-active) {
        opacity: 1;
      }
    }

    &__button-prev,
    &__button-next {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      width: 30px;
      height: 30px;
      z-index: 10;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      border: none;
      padding: 0;
      overflow: visible;

      &:global(.swiper-button-disabled) {
        opacity: 0.35;
        cursor: not-allowed;
        pointer-events: none;
      }

      &::after {
        content: '';
        width: 12px;
        height: 12px;
        border: solid #3B3B3C;
        border-width: 0 2px 2px 0;
        display: inline-block;
        position: relative;
      }
    }

    &__button-prev {
      left: 20px;

      &::after {
        transform: rotate(135deg);
        right: -4px;
      }
    }

    &__button-next {
      right: 20px;

      &::after {
        transform: rotate(-45deg);
        left: -4px;
      }
    }
  }

  .highlighted-stores-carousel {
    margin: 0.75rem auto 0;
    min-height: 20px;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .highlighted-store {
    display: block;
    padding: 2px 0;
    font-size: var(--font-size-text-desktop, 16px);
    letter-spacing: 0.5px;
    line-height: 1.2;
    color: #666;
    text-transform: uppercase;
    white-space: nowrap;
    
    strong {
      font-weight: 600;
    }
  }

  @media screen and (max-width: 768px) {
    .highlighted-store {
      font-size: var(--font-size-text-mobile, 14px);
    }
  }
</style> 